import { toast } from 'react-hot-toast';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { CheckSmallCircleFillIcon, ExclamationmarkSmallCircleFillIcon, XIcon } from '@/icons';
import { Button, IconButton } from '@/ui_v3';

/**
 * 토스트 우측에 선택적으로 노출하는 액션 버튼.
 *
 * @deprecated Use `ToastAction` from `@zep-us/zus/toast` instead.
 */
export type SuccessToastAction = {
  label: string;
  onClick: () => void;
};

/**
 * quiz-app 공통 성공 토스트. 밝은 글래스 표면 + positive 체크 아이콘 + 진한 텍스트.
 * `action` 을 주면 우측에 버튼(예: "클래스로 이동")을 함께 노출한다(기본: 버튼 없음).
 *
 * @deprecated Use `Toast` from `@zep-us/zus/toast` instead.
 */
export function SuccessToast({
  message,
  action
}: {
  message: string;
  action?: SuccessToastAction;
}) {
  return <div className="flex min-h-[48px] items-center gap-lg rounded-sm bg-opacity-white-10 p-sm shadow-lg backdrop-blur-[2px]" data-sentry-component="SuccessToast" data-sentry-source-file="SuccessToast.tsx">
      <div className="flex items-center gap-xs">
        <CheckSmallCircleFillIcon size="md" fill={ICON_COLOR.positive} data-sentry-element="CheckSmallCircleFillIcon" data-sentry-source-file="SuccessToast.tsx" />
        <span className="pr-lg text-body-lg-semibold text-default">
          {message}
        </span>
      </div>
      {action && <Button variant="outlined" intent="neutral" size="sm" onClick={action.onClick}>
          {action.label}
        </Button>}
    </div>;
}
type ErrorToastProps = {
  message: string;
  variant?: 'default';
  description?: never;
  action?: never;
  onClose?: never;
  hasIcon?: never;
} | {
  message: string;
  variant: 'blocking';
  description?: string;
  action?: SuccessToastAction;
  onClose?: () => void;
  hasIcon?: boolean;
};

/**
 * @deprecated Use `Toast` from `@zep-us/zus/toast` instead.
 */
export function ErrorToast(props: ErrorToastProps) {
  const {
    t
  } = useTranslation(['common']);
  if (props.variant !== 'blocking') {
    return <div role="alert" className="flex min-h-[48px] items-center gap-xs rounded-sm bg-opacity-white-10 p-sm shadow-lg backdrop-blur-[2px]">
        <ExclamationmarkSmallCircleFillIcon size="md" fill={ICON_COLOR.destructive} />
        <span className="whitespace-pre-line pr-lg text-body-lg-semibold text-default">
          {props.message}
        </span>
      </div>;
  }
  const {
    message,
    description,
    action,
    onClose,
    hasIcon = true
  } = props;
  return <div role="alert" className="relative flex min-h-[48px] items-center rounded-sm bg-red-50 py-sm pl-lg pr-xl shadow-lg backdrop-blur-[2px]" data-sentry-component="ErrorToast" data-sentry-source-file="SuccessToast.tsx">
      <div className="flex min-w-0 flex-1 items-center gap-sm">
        {hasIcon && <ExclamationmarkSmallCircleFillIcon size="md" fill={ICON_COLOR.destructive} />}
        <div className="min-w-0 flex-1">
          <p className="whitespace-pre-line text-body-lg-semibold text-default">
            {message}
          </p>
          {description && <p className="mt-4xs text-caption-lg-medium text-default">
              {description}
            </p>}
        </div>
      </div>
      {action && <Button variant="outlined" intent="destructive" size="sm" className="ml-lg shrink-0" onClick={action.onClick}>
          {action.label}
        </Button>}
      {onClose && <IconButton variant="transparent" intent="neutral" size="2xs" aria-label={t('common:global.close.button')} className="absolute right-3xs top-3xs" onClick={onClose}>
          <XIcon size="xs" fill={ICON_COLOR.destructive} />
        </IconButton>}
    </div>;
}

/**
 * 성공 토스트를 상단 중앙에 띄운다(react-hot-toast custom 렌더).
 *
 * @deprecated Use `zusToast.success` from `@zep/toast` instead.
 */
export const showSuccessToast = (message: string, options?: {
  action?: SuccessToastAction;
  duration?: number;
}) => toast.custom(<SuccessToast message={message} action={options?.action} />, {
  position: 'top-center',
  duration: options?.duration ?? 2000
});

/**
 * @deprecated Use `zusToast.error` from `@zep/toast` instead.
 */
export const showErrorToast = (message: string, options?: {
  duration?: number;
}) => toast.custom(<ErrorToast message={message} />, {
  position: 'top-center',
  duration: options?.duration ?? 2000
});